import { atom } from "nanostores";
import type { InspectReport } from "../net/api";

export interface ClipInfo { name: string; duration: number }
export const SPEEDS = [0.25, 0.5, 1, 2] as const;
export type Speed = (typeof SPEEDS)[number];
export interface InspectorState { clip: number; playing: boolean; time: number; speed: Speed; skeleton: boolean; grid: boolean; ruler: boolean }
export interface InspectorView {
  code: string; name: string; status: "loading" | "ready" | "failed";
  clips: ClipInfo[]; hasSkeleton: boolean; report: InspectReport | null; state: InspectorState;
}

/** The model on the turntable, or null when the inspector is closed (spec 2o §2.4). */
export const $inspector = atom<InspectorView | null>(null);

export function initialState(clips: readonly ClipInfo[]): InspectorState {
  const clip = clips.length > 0 ? 0 : -1;
  return { clip, playing: clip >= 0 && clips[0]!.duration > 0, time: 0, speed: 1, skeleton: false, grid: true, ruler: false };
}

export function selectClip(s: InspectorState, i: number, clips: readonly ClipInfo[]): InspectorState {
  if (i < 0 || i >= clips.length) return s;
  return { ...s, clip: i, time: 0, playing: clips[i]!.duration > 0 };
}

export function togglePlay(s: InspectorState, clips: readonly ClipInfo[]): InspectorState {
  const c = clips[s.clip];
  return c && c.duration > 0 ? { ...s, playing: !s.playing } : s;
}

/** Dragging the timeline pauses the clip at t seconds, clamped to the clip. */
export function scrub(s: InspectorState, t: number, clips: readonly ClipInfo[]): InspectorState {
  const c = clips[s.clip];
  return c && c.duration > 0 ? { ...s, playing: false, time: Math.min(Math.max(t, 0), c.duration) } : s;
}

export function setSpeed(s: InspectorState, speed: Speed): InspectorState { return { ...s, speed }; }

export function toggle(s: InspectorState, key: "skeleton" | "grid" | "ruler"): InspectorState { return { ...s, [key]: !s[key] }; }

/** Applies fn to the open inspector's state; nothing happens when none is open. */
export function updateState(fn: (s: InspectorState, clips: readonly ClipInfo[]) => InspectorState): void {
  const v = $inspector.get();
  if (!v) return;
  const next = fn(v.state, v.clips);
  if (next !== v.state) $inspector.set({ ...v, state: next });
}
